import React, { useState, useRef, useEffect } from 'react';
import type { SearchQuery, LocationOption } from '../types';
import { SearchType } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { getArrivalAutoComplete, searchTourvisio } from '../services/tourvisioService';
import { CURRENCIES, NIGHTS } from '../constants';
import { useDebounce } from '../hooks/useDebounce';

// --- Icons (reused from SearchWidget) ---
const ChevronDownIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
)
const MapPinIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
)
const CalendarIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
)
const UserGroupIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
)
const MoonIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
)
const SearchIconSvg: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
)
const HomeIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
)

const CompactDropdown: React.FC<{
    label: string, value: string, displayValue: string,
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void,
    children: React.ReactNode, disabled?: boolean, loading?: boolean,
    icon?: React.ReactNode, className?: string
}> = ({ label, value, displayValue, onChange, children, disabled, loading, icon, className }) => (
    <div className={`relative group w-full h-full flex flex-col justify-center bg-white border border-gray-200 lg:border-transparent transition-colors hover:bg-gray-50 rounded-xl ${className}`}>
        <select value={value} onChange={onChange} disabled={disabled || loading}
            className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer disabled:cursor-not-allowed appearance-none">
            {loading ? <option>...</option> : children}
        </select>
        <div className="flex items-center gap-2 px-3 py-2 pointer-events-none">
            <div className="flex-shrink-0 text-gray-400 group-hover:text-black transition-colors scale-90">{icon}</div>
            <div className="flex-grow min-w-0 overflow-hidden">
                <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5 truncate">{label}</label>
                <div className="font-bold text-sm text-black truncate pr-2">{loading ? "..." : displayValue}</div>
            </div>
            <div className="flex-shrink-0 text-gray-400"><ChevronDownIcon /></div>
        </div>
    </div>
);

interface HotelSearchWidgetProps {
    onSearch: (query: SearchQuery) => void;
    logApiCall: (log: any) => void;
    isSettingsReady: boolean;
}

/**
 * Hotel-Only Search Widget
 * Uses Tourvisio Hotel Product API (ProductType 2)
 * No departure field — only arrival/destination, check-in, nights, guests
 * Per docs: https://docs.santsg.com/tourvisio/hproduct/
 */
export const HotelSearchWidget: React.FC<HotelSearchWidgetProps> = ({ onSearch, logApiCall, isSettingsReady }) => {
    const { t, language } = useLocalization();

    const getFutureDate = (daysToAdd: number) => {
        const date = new Date();
        date.setDate(date.getDate() + daysToAdd);
        return date.toISOString().split('T')[0];
    };

    // --- State ---
    const [destinationId, setDestinationId] = useState('');
    const [destinationSearch, setDestinationSearch] = useState('');
    const [checkIn, setCheckIn] = useState(getFutureDate(30));
    const [nights, setNights] = useState<number | ''>(7);
    const [nationality, setNationality] = useState('SE');
    const [currency, setCurrency] = useState('SEK');
    const [rooms, setRooms] = useState(1);
    const [adults, setAdults] = useState(2);
    const [childrenCount, setChildrenCount] = useState(0);
    const [childrenAges, setChildrenAges] = useState<number[]>([]);

    const [arrivalOptions, setArrivalOptions] = useState<LocationOption[]>([]);
    const [isLoadingArrivals, setIsLoadingArrivals] = useState(false);
    const [isGuestSelectorOpen, setIsGuestSelectorOpen] = useState(false);
    const [showDestDropdown, setShowDestDropdown] = useState(false);
    const guestSelectorRef = useRef<HTMLDivElement>(null);
    const destDropdownRef = useRef<HTMLDivElement>(null);

    const debouncedDestSearch = useDebounce(destinationSearch, 400);
    const getCulture = () => language === 'sv' ? 'sv-SE' : 'en-US';

    // --- Fetch arrivals using GetArrivalAutocomplete (PT:2) ---
    useEffect(() => {
        if (!isSettingsReady) return;

        const fetchArrivals = async () => {
            // API requires min 3 characters
            const query = debouncedDestSearch && debouncedDestSearch.length >= 3
                ? debouncedDestSearch
                : 'tur'; // default: popular destinations

            setIsLoadingArrivals(true);
            try {
                const results = await getArrivalAutoComplete(query, 2, getCulture(), logApiCall);

                // Map autocomplete items to LocationOption format
                // API items may have: { id, name, type } or nested: { city: {id, name}, country: {id, name} }
                const options: LocationOption[] = results
                    .map((item: any) => {
                        const id = String(item.city?.id ?? item.state?.id ?? item.country?.id ?? item.id ?? '');
                        const name = item.city?.name || item.state?.name || item.country?.name || item.name || '';
                        const type = item.city ? 2 : item.country ? 1 : (item.type ?? 2);
                        const parentId = item.country?.id ? String(item.country.id) : '';
                        return { id, name, type, parentId };
                    })
                    .filter((opt: LocationOption) => opt.id && opt.name);
                setArrivalOptions(options);
            } catch (error) {
                console.error('Failed to fetch hotel arrivals:', error);
                setArrivalOptions([]);
            } finally {
                setIsLoadingArrivals(false);
            }
        };
        fetchArrivals();
    }, [debouncedDestSearch, isSettingsReady, logApiCall, language]);

    // Close guest selector and destination dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (guestSelectorRef.current && !guestSelectorRef.current.contains(event.target as Node)) {
                setIsGuestSelectorOpen(false);
            }
            if (destDropdownRef.current && !destDropdownRef.current.contains(event.target as Node)) {
                setShowDestDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleChildrenChange = (newCount: number) => {
        setChildrenCount(newCount);
        const newAges = [...childrenAges];
        if (newCount > newAges.length) {
            for (let i = newAges.length; i < newCount; i++) newAges.push(8);
        } else if (newCount < newAges.length) {
            newAges.length = newCount;
        }
        setChildrenAges(newAges);
    };

    const handleChildAgeChange = (index: number, age: number) => {
        const newAges = [...childrenAges];
        newAges[index] = age;
        setChildrenAges(newAges);
    };

    const getSelectedArrival = (id: string) => arrivalOptions.find(a => String(a.id) === String(id));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!destinationId || typeof nights !== 'number') return;

        const destOpt = getSelectedArrival(destinationId);
        onSearch({
            type: SearchType.OnlyHotel,
            departureId: '', // No departure for hotel-only
            destinationId,
            destinationName: destOpt?.name,
            destinationType: destOpt?.type || 2,
            checkIn,
            nights,
            rooms,
            nationality,
            currency,
            adults,
            children: childrenCount,
            childrenAges
        });
    };

    const guestSummary = `${adults} ${t('adults')}, ${childrenCount} ${t('children')}`;
    const today = new Date().toISOString().split('T')[0];

    const getFormattedDate = (dateStr: string) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        return d.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getCheckoutDate = () => {
        if (!checkIn || !nights) return "-";
        const d = new Date(checkIn);
        d.setDate(d.getDate() + Number(nights));
        return d.toLocaleDateString(language === 'sv' ? 'sv-SE' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    // Group arrival options by country
    const renderLocationOptions = (options: LocationOption[]) => {
        const countries = options.filter(opt => opt.type === 1);
        const cities = options.filter(opt => opt.type === 2);

        if (countries.length === 0) {
            return options.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
            ));
        }
        return (
            <>
                {countries.map(country => {
                    const countryCities = cities.filter(city => city.parentId === country.id);
                    if (countryCities.length === 0) return null;
                    return (
                        <optgroup key={country.id} label={country.name}>
                            {countryCities.map(city => (
                                <option key={city.id} value={city.id}>{city.name}</option>
                            ))}
                        </optgroup>
                    );
                })}
                {cities.filter(city => !countries.find(c => c.id === city.parentId)).map(city => (
                    <option key={city.id} value={city.id}>{city.name}</option>
                ))}
            </>
        );
    };

    return (
        <div className="w-full max-w-[90rem] mx-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-2 ring-1 ring-black/5">

                {/* Live Preview */}
                {nights !== '' && destinationId && (
                    <div className="hidden md:flex items-center justify-between px-6 py-3 bg-emerald-50/50 rounded-xl mb-2 text-sm font-medium text-emerald-900 border border-emerald-100 animate-fade-in">
                        <div className="flex items-center gap-8">
                            <div className="flex items-center gap-2">
                                <CalendarIcon />
                                <span className="flex flex-col leading-tight">
                                    <span className="font-bold uppercase text-[10px] tracking-wider text-emerald-700 opacity-80">{t('check_in_label')}</span>
                                    <span>{getFormattedDate(checkIn)}</span>
                                </span>
                                <span className="mx-2 text-emerald-300">→</span>
                                <span className="flex flex-col leading-tight">
                                    <span className="font-bold uppercase text-[10px] tracking-wider text-emerald-700 opacity-80">{t('check_out_label')}</span>
                                    <span>{getCheckoutDate()}</span>
                                </span>
                            </div>
                            <div className="h-8 w-px bg-emerald-200/50 mx-2"></div>
                            <div className="flex items-center gap-2">
                                <MoonIcon />
                                <span className="flex flex-col leading-tight">
                                    <span className="font-bold uppercase text-[10px] tracking-wider text-emerald-700 opacity-80">{t('duration_label')}</span>
                                    <span>{nights} {t('nights')}</span>
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <UserGroupIcon />
                            <span className="flex flex-col leading-tight text-right">
                                <span className="font-bold uppercase text-[10px] tracking-wider text-emerald-700 opacity-80">{t('travelers_label')}</span>
                                <span>{adults + childrenCount} ({adults} {t('adults')}, {childrenCount} {t('children')}) - {rooms} {t('rooms_label')}</span>
                            </span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:flex lg:flex-row lg:items-stretch gap-2 lg:gap-0 lg:divide-x divide-gray-200">

                    {/* Destination - Autocomplete input */}
                    <div className="lg:w-[25%] relative" ref={destDropdownRef}>
                        <div className="relative group w-full h-full flex flex-col justify-center bg-white border border-gray-200 lg:border-transparent transition-colors hover:bg-gray-50 rounded-xl">
                            <div className="flex items-center gap-2 px-3 py-2">
                                <div className="flex-shrink-0 text-gray-400 group-hover:text-black transition-colors scale-90"><MapPinIcon /></div>
                                <div className="flex-grow min-w-0 overflow-hidden">
                                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5 truncate">{t('destination')}</label>
                                    <input
                                        type="text"
                                        value={destinationSearch}
                                        onChange={(e) => {
                                            setDestinationSearch(e.target.value);
                                            setShowDestDropdown(true);
                                            if (!e.target.value) setDestinationId('');
                                        }}
                                        onFocus={() => setShowDestDropdown(true)}
                                        placeholder={t('choose')}
                                        autoComplete="off"
                                        className="w-full bg-transparent text-black font-bold text-sm focus:outline-none p-0 placeholder-gray-400"
                                    />
                                </div>
                                {isLoadingArrivals ? (
                                    <div className="flex-shrink-0 animate-spin rounded-full h-4 w-4 border-t-2 border-emerald-500"></div>
                                ) : (
                                    <div className="flex-shrink-0 text-gray-400 cursor-pointer" onClick={() => setShowDestDropdown(!showDestDropdown)}><ChevronDownIcon /></div>
                                )}
                            </div>
                        </div>

                        {/* Autocomplete dropdown */}
                        {showDestDropdown && arrivalOptions.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white shadow-2xl rounded-xl border border-gray-100 z-50 max-h-64 overflow-y-auto animate-fade-in-up">
                                {(() => {
                                    const countries = arrivalOptions.filter(o => o.type === 1);
                                    const cities = arrivalOptions.filter(o => o.type === 2);
                                    if (countries.length === 0) {
                                        return arrivalOptions.map(opt => (
                                            <button key={opt.id} type="button"
                                                onClick={() => { setDestinationId(String(opt.id)); setDestinationSearch(opt.name); setShowDestDropdown(false); }}
                                                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 ${String(opt.id) === destinationId ? 'bg-emerald-50 font-bold text-emerald-700' : 'text-gray-700'}`}>
                                                <MapPinIcon /><span>{opt.name}</span>
                                            </button>
                                        ));
                                    }
                                    return countries.map(country => {
                                        const countryCities = cities.filter(c => c.parentId === country.id);
                                        if (countryCities.length === 0) return null;
                                        return (
                                            <div key={country.id}>
                                                <div className="px-4 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 sticky top-0">{country.name}</div>
                                                {countryCities.map(city => (
                                                    <button key={city.id} type="button"
                                                        onClick={() => { setDestinationId(String(city.id)); setDestinationSearch(city.name); setShowDestDropdown(false); }}
                                                        className={`w-full text-left px-4 py-2.5 pl-6 text-sm hover:bg-emerald-50 transition-colors flex items-center gap-2 ${String(city.id) === destinationId ? 'bg-emerald-50 font-bold text-emerald-700' : 'text-gray-700'}`}>
                                                        <MapPinIcon /><span>{city.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        )}
                    </div>

                    {/* Check-in Date - 15% */}
                    <div className="lg:w-[15%]">
                        <div className="relative group w-full h-full flex flex-col justify-center px-3 py-2 bg-white hover:bg-gray-50 rounded-xl transition-colors border border-gray-200 lg:border-transparent">
                            <div className="flex items-center gap-2">
                                <div className="text-gray-400 group-hover:text-black transition-colors scale-90"><CalendarIcon /></div>
                                <div className="flex-grow">
                                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">{t('check_in_date')}</label>
                                    <input type="date" value={checkIn} min={today} onChange={(e) => setCheckIn(e.target.value)}
                                        className="w-full bg-transparent text-black font-bold text-sm focus:outline-none p-0" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Nights - 10% */}
                    <div className="lg:w-[10%]">
                        <CompactDropdown label={t('night')} value={String(nights)} displayValue={nights ? String(nights) : t('choose')}
                            onChange={e => setNights(Number(e.target.value))} icon={<MoonIcon />}>
                            {NIGHTS.map(n => <option key={n} value={n}>{n}</option>)}
                        </CompactDropdown>
                    </div>

                    {/* Rooms - 10% */}
                    <div className="lg:w-[10%]">
                        <CompactDropdown label={t('rooms_label')} value={String(rooms)} displayValue={String(rooms)}
                            onChange={e => setRooms(Number(e.target.value))} icon={<HomeIcon />}>
                            {[1, 2, 3].map(r => <option key={r} value={r}>{r}</option>)}
                        </CompactDropdown>
                    </div>

                    {/* Guests - 15% */}
                    <div className="lg:w-[15%] relative" ref={guestSelectorRef}>
                        <button type="button" onClick={() => setIsGuestSelectorOpen(!isGuestSelectorOpen)}
                            className="w-full h-full flex items-center px-3 py-2 bg-white border border-gray-200 lg:border-transparent hover:bg-gray-50 rounded-xl transition-colors text-left group focus:outline-none">
                            <div className="flex items-center gap-2 w-full">
                                <div className="text-gray-400 group-hover:text-black transition-colors scale-90"><UserGroupIcon /></div>
                                <div className="flex-grow min-w-0">
                                    <label className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">{t('guests')}</label>
                                    <span className="font-bold text-black text-sm block truncate">{guestSummary}</span>
                                </div>
                                <div className="text-gray-400"><ChevronDownIcon /></div>
                            </div>
                        </button>

                        {isGuestSelectorOpen && (
                            <div className="absolute top-full right-0 mt-3 w-80 bg-white shadow-2xl rounded-2xl border border-gray-100 z-50 p-6 animate-fade-in-up">
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <div><p className="font-bold text-black">{t('adults')}</p><p className="text-xs text-gray-500 font-medium">{t('ages_18_plus')}</p></div>
                                        <div className="flex items-center gap-3">
                                            <button type="button" onClick={() => setAdults(Math.max(1, adults - 1))} className="w-8 h-8 rounded-full border border-gray-200 hover:border-black flex items-center justify-center font-bold">-</button>
                                            <span className="w-4 text-center font-bold text-lg">{adults}</span>
                                            <button type="button" onClick={() => { if (adults + childrenCount < 9) setAdults(adults + 1); }} disabled={adults + childrenCount >= 9} className="w-8 h-8 rounded-full border border-gray-200 hover:border-black flex items-center justify-center font-bold disabled:opacity-50">+</button>
                                        </div>
                                    </div>
                                    <hr className="border-gray-100" />
                                    <div className="flex items-center justify-between">
                                        <div><p className="font-bold text-black">{t('children')}</p><p className="text-xs text-gray-500 font-medium">{t('ages_0_17')}</p></div>
                                        <div className="flex items-center gap-3">
                                            <button type="button" onClick={() => handleChildrenChange(Math.max(0, childrenCount - 1))} className="w-8 h-8 rounded-full border border-gray-200 hover:border-black flex items-center justify-center font-bold">-</button>
                                            <span className="w-4 text-center font-bold text-lg">{childrenCount}</span>
                                            <button type="button" onClick={() => { if (adults + childrenCount < 9) handleChildrenChange(childrenCount + 1); }} disabled={adults + childrenCount >= 9} className="w-8 h-8 rounded-full border border-gray-200 hover:border-black flex items-center justify-center font-bold disabled:opacity-50">+</button>
                                        </div>
                                    </div>
                                    {childrenCount > 0 && (
                                        <div className="pt-2 space-y-3">
                                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide">{t('child_ages')}</p>
                                            <div className="grid grid-cols-2 gap-3">
                                                {childrenAges.map((age, index) => (
                                                    <div key={index}>
                                                        <label className="block text-[10px] font-bold text-gray-500 mb-1">{t('child_age').replace('{0}', String(index + 1))}</label>
                                                        <select value={age} onChange={(e) => handleChildAgeChange(index, Number(e.target.value))}
                                                            className="w-full border border-gray-200 bg-white rounded-lg p-2 text-sm font-semibold focus:border-black outline-none">
                                                            {[...Array(18)].map((_, i) => <option key={i} value={i}>{i} {t('years_old')}</option>)}
                                                        </select>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Currency - 15% */}
                    <div className="hidden lg:block lg:w-[15%]">
                        <CompactDropdown label={t('currency')} value={currency} displayValue={currency}
                            onChange={e => setCurrency(e.target.value)} icon={<span className="font-bold text-sm">{currency === 'EUR' ? '€' : 'kr'}</span>}>
                            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                        </CompactDropdown>
                    </div>

                    {/* Submit - 10% */}
                    <div className="col-span-1 md:col-span-2 lg:w-[10%] p-1">
                        <button type="submit"
                            className="w-full h-full min-h-[56px] bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed group"
                            disabled={!destinationId || !nights}>
                            <SearchIconSvg />
                            <span className="ml-2 font-bold lg:hidden">{t('search')}</span>
                        </button>
                    </div>
                </form>

                {/* Mobile Summary */}
                <div className="md:hidden mt-2 px-2 pb-2 text-xs text-gray-500 text-center">
                    {nights !== '' && destinationId ? (
                        <>
                            {getFormattedDate(checkIn)} - {getCheckoutDate()} ({nights} {t('nights')}) • {adults + childrenCount} {t('travelers_label')}
                        </>
                    ) : (
                        <span className="text-gray-400 italic">{t('select_duration_hint')}</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HotelSearchWidget;
